import type {
  AppointmentPlanningExistingPlannedRow,
  AppointmentPlanningExistingSourceRow,
} from "../../../../../../../../../../../_shared/_logic/appointments-contract"
import { formatDateOnly } from "../../../../../../../../../../../_shared/_logic/dates"
import type {
  AppointmentPlanningCommitResponse,
  AppointmentPlanningPreviewAppointment,
  AppointmentPlanningPreviewRow,
  AppointmentPlanningRowResult,
  AppointmentPlanningStrategy,
} from "../../../../../../../../../../../../../api/kehrblatt/planning"
import type {
  InboxCaseBatchActionResult,
  InboxCaseBatchWorkflow,
  InboxCaseBatchWorkflowItem,
} from "../../../../../../../../../../../../../api/kehrblatt/inbox-cases"
import type { ScheduleEventStatus } from "../../../../../../../../../../../../../_shared/_logic/enums"
import { workflowItemOutputFor } from "../../../_shared/_logic/table-rows"
import { isRecord, objectValue, stringValue } from "./sourceRows"

const STEP_KEY = "plan_appointment"

export type PlannedAppointmentStepOutput = {
  work_order_id: string
  schedule_event_id?: string | null
  source_schedule_event_id?: string | null
  canceled_schedule_event_id?: string | null
  status?: string | null
  start_at?: string | null
  end_at?: string | null
  employee_id?: string | null
  employee_name?: string | null
  appointment_contact_customer_id?: string | null
  contact_order_id?: string | null
  first_contact_step_id?: string | null
  preview_appointment?: AppointmentPlanningPreviewAppointment | null
  decision?: "PLANNED" | "SKIPPED" | "FAILED" | string | null
  reason?: string | null
}

export const plannedOutputFromStep = (
  item: InboxCaseBatchWorkflowItem,
): PlannedAppointmentStepOutput | null => {
  const projectedOutput = workflowItemOutputFor(item, STEP_KEY)
  const candidate = objectValue(projectedOutput)
  const workOrderId = stringValue(candidate.work_order_id)

  if (!workOrderId) return null

  const previewAppointment = isRecord(candidate.preview_appointment)
    ? (candidate.preview_appointment as unknown as AppointmentPlanningPreviewAppointment)
    : null

  return {
    work_order_id: workOrderId,
    schedule_event_id: stringValue(candidate.schedule_event_id) || null,
    source_schedule_event_id: stringValue(candidate.source_schedule_event_id) || null,
    canceled_schedule_event_id: stringValue(candidate.canceled_schedule_event_id) || null,
    status: stringValue(candidate.status) || null,
    start_at: stringValue(candidate.start_at) || null,
    end_at: stringValue(candidate.end_at) || null,
    employee_id: stringValue(candidate.employee_id) || null,
    employee_name: stringValue(candidate.employee_name) || null,
    appointment_contact_customer_id: stringValue(candidate.appointment_contact_customer_id) || null,
    contact_order_id: stringValue(candidate.contact_order_id) || null,
    first_contact_step_id: stringValue(candidate.first_contact_step_id) || null,
    preview_appointment: previewAppointment,
    decision: stringValue(candidate.decision) || null,
    reason: stringValue(candidate.reason) || null,
  }
}

const previewRowFromPlannedOutput = (
  output: PlannedAppointmentStepOutput,
  source: AppointmentPlanningExistingSourceRow,
): AppointmentPlanningPreviewRow | null => {
  if (!output.preview_appointment) return null

  const strategies: AppointmentPlanningStrategy[] = source.previousEvent
    ? ["AUTO", "MANUAL", "KEEP_EXISTING"]
    : ["AUTO", "MANUAL"]

  return {
    client_row_id: source.id,
    status: "planned",
    appointment: output.preview_appointment,
    warnings: [],
    available_planning_strategies: strategies,
    active_schedule_event: null,
    effective_planning_strategy: source.defaultPlanningStrategy ?? "AUTO",
    manual_slot_options: [],
    resolved_appointment_contact: null,
  }
}

const scheduleEventFromPlannedOutput = (
  output: PlannedAppointmentStepOutput,
  source: AppointmentPlanningExistingSourceRow,
  organizationId: string,
): ScheduleEvent | null => {
  if (!output.schedule_event_id || !output.start_at || !output.end_at) return null

  const now = new Date().toISOString()

  return {
    id: output.schedule_event_id,
    can_manage: true,
    organization_id: stringValue(source.workOrder.organization_id, organizationId),
    employee_id: output.employee_id ?? null,
    employee_name: output.employee_name ?? null,
    event_type: "APPOINTMENT",
    flex_type: "HARD",
    status: (output.status || "RESERVED") as ScheduleEventStatus,
    planning_flag: "OK",
    start_at: output.start_at,
    end_at: output.end_at,
    timeframe_start_at: output.start_at,
    timeframe_end_at: output.end_at,
    all_day: false,
    title: null,
    description: null,
    customer_note: null,
    location_text: null,
    property_id: source.workOrder.property_id,
    work_order_id: output.work_order_id,
    source: "SYSTEM",
    calendar_sync: null,
    created_at: now,
    updated_at: now,
    contact_order_id: output.contact_order_id ?? null,
  }
}

export const buildInitialPlannedRows = ({
  workflow,
  sourceRows,
  organizationId,
}: {
  workflow: InboxCaseBatchWorkflow
  sourceRows: AppointmentPlanningExistingSourceRow[]
  organizationId: string
}): AppointmentPlanningExistingPlannedRow[] => {
  const sourceByWorkOrderId = new Map(sourceRows.map((row) => [row.workOrder.id, row]))

  return workflow.items
    .map((item) => {
      const output = plannedOutputFromStep(item)

      if (!output) return null

      const source = sourceByWorkOrderId.get(output.work_order_id)

      if (!source) return null

      const preview = previewRowFromPlannedOutput(output, source)

      return {
        id: source.id,
        source,
        preview,
        event: preview ? null : scheduleEventFromPlannedOutput(output, source, organizationId),
      } satisfies AppointmentPlanningExistingPlannedRow
    })
    .filter((row): row is AppointmentPlanningExistingPlannedRow => row !== null)
}

const plannedOutputFromCommitRow = (
  row: Record<string, unknown>,
): PlannedAppointmentStepOutput | null => {
  const output = objectValue(row.output)
  const candidate = objectValue(output.planned_appointment || output)
  const workOrderId = stringValue(candidate.work_order_id)

  if (!workOrderId) return null

  const previewAppointment = isRecord(candidate.preview_appointment)
    ? (candidate.preview_appointment as unknown as AppointmentPlanningPreviewAppointment)
    : null

  return {
    work_order_id: workOrderId,
    schedule_event_id: stringValue(candidate.schedule_event_id) || null,
    source_schedule_event_id: stringValue(candidate.source_schedule_event_id) || null,
    canceled_schedule_event_id: stringValue(candidate.canceled_schedule_event_id) || null,
    status: stringValue(candidate.status) || null,
    start_at: stringValue(candidate.start_at) || null,
    end_at: stringValue(candidate.end_at) || null,
    employee_id: stringValue(candidate.employee_id) || null,
    employee_name: stringValue(candidate.employee_name) || null,
    appointment_contact_customer_id: stringValue(candidate.appointment_contact_customer_id) || null,
    contact_order_id: stringValue(candidate.contact_order_id) || null,
    first_contact_step_id: stringValue(candidate.first_contact_step_id) || null,
    preview_appointment: previewAppointment,
    decision: stringValue(candidate.decision) || null,
    reason: stringValue(candidate.reason) || null,
  }
}

const itemRowForActionCaseId = (
  workflow: InboxCaseBatchWorkflow,
  sourceRows: AppointmentPlanningExistingSourceRow[],
) => {
  const sourceByActionCaseId = new Map(sourceRows.map((row) => [row.id, row]))

  return new Map(
    workflow.items
      .map((item) => {
        const source = sourceByActionCaseId.get(item.action_case.id)

        return source ? ([item.action_case.id, { item, source }] as const) : null
      })
      .filter(
        (
          entry,
        ): entry is readonly [
          string,
          {
            item: InboxCaseBatchWorkflowItem
            source: AppointmentPlanningExistingSourceRow
          },
        ] => entry !== null,
      ),
  )
}

export const buildPlanningResponseFromInboxResult = ({
  workflow,
  result,
  sourceRows,
  organizationId,
}: {
  workflow: InboxCaseBatchWorkflow
  result: InboxCaseBatchActionResult
  sourceRows: AppointmentPlanningExistingSourceRow[]
  organizationId: string
}): AppointmentPlanningCommitResponse => {
  const actionCaseSource = itemRowForActionCaseId(workflow, sourceRows)
  const scheduledEvents: ScheduleEvent[] = []
  const rowResults: AppointmentPlanningRowResult[] = []
  const failed: AppointmentPlanningRowResult[] = []
  const impactedDateSet = new Set<string>()
  for (const row of result.rows) {
    const source = actionCaseSource.get(row.action_case_id)?.source ?? null

    if (row.status === "success") {
      const planned = plannedOutputFromCommitRow(row as unknown as Record<string, unknown>)

      if (planned && source) {
        const event = scheduleEventFromPlannedOutput(planned, source, organizationId)

        if (event) {
          scheduledEvents.push(event)
          impactedDateSet.add(formatDateOnly(event.start_at))
          rowResults.push({
            client_row_id: source.id,
            status: "planned",
            work_order_id: source.workOrder.id,
            schedule_event_id: event.id,
          })
        }

        continue
      }
    }

    const failedRow: AppointmentPlanningRowResult = {
      client_row_id: source?.id ?? row.action_case_id,
      status: "failed",
      work_order_id: source?.workOrder.id ?? "",
      reason: row.error || row.detail || "Termin konnte nicht geplant werden.",
    }
    failed.push(failedRow)
    rowResults.push(failedRow)
  }

  return {
    scheduled_events: scheduledEvents,
    row_results: rowResults,
    failed,
    impacted_dates: [...impactedDateSet],
  }
}
